/**
 * A dropped page seen whole: the screenshot at a readable width in its own
 * scrolling column, the six lines read from it beside it. Esc and Close put it
 * away, focus stays inside while it is open and goes back to where it was.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { PageInspiration, StructureBrief } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { ShotLines } from "./ShotLines";
import type { ReadState } from "./useShotReads";

const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function ShotViewer({
  letter,
  one,
  state,
  onClose,
}: {
  letter: string;
  one: PageInspiration;
  state?: ReadState["state"];
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>(".nbview-x")?.focus({ preventScroll: true });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      before?.focus?.({ preventScroll: true });
    };
  }, []);

  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close.current();
      return;
    }
    if (e.key !== "Tab" || !box.current) return;
    const all = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = all[0];
    const last = all[all.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const brief: StructureBrief | undefined = one.brief;
  return createPortal(
    <div className="nbview" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className="nbview-box" role="dialog" aria-modal="true" aria-label={`Page ${letter}, the whole page`} onKeyDown={keys}>
        <div className="nbview-pic" tabIndex={0} aria-label="The whole page, scrolls">
          <img src={one.url} alt={`The page you dropped for design ${letter}`} />
        </div>
        <aside className="nbview-side">
          <div className="nbview-h">
            <span className="nbshot-l">{letter}</span>
            <span className="sp nbview-n">{one.name}</span>
            <button type="button" className="ibtn nbview-x" aria-label="Close" onClick={onClose}>
              <Icon name="x" />
            </button>
          </div>
          <ShotLines letter={letter} brief={brief} state={state} />
        </aside>
      </div>
    </div>,
    document.body,
  );
}
